/* eslint-disable max-lines -- App.tsx wires the global state machines
 * (scope, channel, view, multiplex) and is at the file-size ceiling.
 * Refactor candidate: extract `openChannelWithContext` and related
 * setters into a dedicated `useChannelNavigation` hook. */
import { startTransition, useCallback, useMemo, useRef } from "react";
import { useConnectionState, useZero } from "@rocicorp/zero/react";
import { CurrentUserProvider, useCurrentUser } from "@/lib/current-user";
import { useMutedSet, useMyMemberships } from "@/lib/selectors";
import { useNotifications } from "@/hooks/useNotifications";
import { useChromeBadge } from "@/hooks/use-chrome-badge";
import { useRequestNotificationPermission } from "@/hooks/use-request-notification-permission";
import { useConnectionStatusToasts } from "@/hooks/use-connection-status-toasts";
import { useUpdatePrompts } from "@/hooks/useUpdatePrompts";
import { useCommandShortcuts } from "@/hooks/useCommandShortcuts";
import { useMenuCommandListener } from "@/hooks/useMenuCommandListener";
import { runUpdateCheck } from "@/lib/electron-updater-toast";
import { csMark, csPeekClickSource } from "@/lib/channel-switch-profile";
import {
  CommandProvider,
  useRegisterCommand,
} from "@/lib/commands/CommandProvider";
import { ProfileInitializationState } from "@/components/auth/ProfileInitializationState";
import { PublicWorkspaceSplash } from "@/components/workspace/PublicWorkspaceSplash";
import {
  generateSlugFromName,
  normalizeWorkspaceSlug,
} from "@/lib/workspace-validation";
import { workspaceSlugFromUrl, appOrigin } from "@/lib/app-route";
import type { CommandContext } from "@/lib/commands/types";
import { AuthGate } from "@/components/app/AuthGate";
import { AppShell } from "@/components/app/AppShell";
import { AppDialogs } from "@/components/app/AppDialogs";
import { FirstRunGate } from "@/components/app/FirstRunGate";
import { useFirstRunGate } from "@/components/app/use-first-run-gate";
import { useShellDeepLinkListener } from "@/components/app/use-shell-deep-link-listener";
import {
  AuthLoadingFullscreen,
  AuthTimeoutScreen,
} from "@/components/app/auth-screens";
import { useViewMode } from "@/components/app/use-view-mode";
import { useTopLevelView } from "@/components/app/use-top-level-view";
import { useChannelNavigation } from "@/components/app/use-channel-navigation";
import { useAppUrlSync } from "@/components/app/use-app-url-sync";
import { useActiveView } from "@/components/app/use-active-view";
import { useMultiplexMountedIds } from "@/components/app/use-multiplex-mounted-ids";
import { useWorkspaceScope } from "@/components/app/use-workspace-scope";
import {
  useInviteFlow,
  useInviteToken,
} from "@/components/app/use-invite-flow";
import { useWarmPreload } from "@/components/app/use-warm-preload";
import { useCliCredentialsInstall } from "@/components/app/use-cli-credentials-install";
import { useDialogState } from "@/components/app/use-dialog-state";
import { useAppCrossCuttingEffects } from "@/components/app/use-app-cross-cutting-effects";
import { useAutoJoinAfterAuth } from "@/hooks/use-auto-join-after-auth";
import { usePendingDraftReply } from "@/hooks/use-pending-draft-reply";
import { CallProvider } from "@/components/calls/CallProvider";
import { AppCallDialog } from "@/components/calls/AppCallDialog";

function AuthenticatedApp() {
  return (
    <CurrentUserProvider>
      <CallProvider>
        <AppInner />
        <AppCallDialog />
      </CallProvider>
    </CurrentUserProvider>
  );
}

function AppCommandBindings({
  onOpenCommandPalette,
  onCycleViewMode,
  onToggleTerminal,
  onOpenKeyboardShortcuts,
  onCheckForUpdates,
}: {
  onOpenCommandPalette: () => void;
  onCycleViewMode: () => void;
  onToggleTerminal: () => void;
  onOpenKeyboardShortcuts: () => void;
  onCheckForUpdates: () => void;
}) {
  useRegisterCommand("app.openCommandPalette", onOpenCommandPalette);
  useRegisterCommand("view.cycleMode", onCycleViewMode);
  useRegisterCommand("view.toggleTerminal", onToggleTerminal);
  useRegisterCommand("help.openKeyboardShortcuts", onOpenKeyboardShortcuts);
  useRegisterCommand("app.checkForUpdates", onCheckForUpdates);
  useRegisterCommand("help.openDocs", () =>
    openExternalSafe("https://ano.dev"),
  );
  useRegisterCommand("help.reportIssue", () =>
    openExternalSafe("https://github.com/LeoNilsson/project-ano/issues/new"),
  );
  useRegisterCommand("help.sendFeedback", () =>
    openExternalSafe("mailto:feedback@ano.dev"),
  );
  useCommandShortcuts();
  useMenuCommandListener();
  return null;
}

function openExternalSafe(url: string) {
  const api = (
    window as unknown as {
      __electronApi?: { openExternalUrl?: (u: string) => Promise<unknown> };
    }
  ).__electronApi;
  if (api?.openExternalUrl) {
    api.openExternalUrl(url).catch(() => {
      window.open(url, "_blank", "noopener,noreferrer");
    });
  } else {
    window.open(url, "_blank", "noopener,noreferrer");
  }
}

function AppInner() {
  const { user, loading, logout, loggingOut } = useCurrentUser();
  const [mutedSet] = useMutedSet();
  const [myMemberships] = useMyMemberships();
  const zero = useZero();

  // ─── State machines (resolution order matters; see SKILL.md) ─────────
  // Invite token first — workspace scope reads it.
  const [inviteToken, setInviteToken] = useInviteToken();
  const {
    selectedChannel,
    setSelectedChannel,
    targetMessageId,
    setTargetMessageId,
    targetThreadId,
    setTargetThreadId,
    handleTargetMessageHandled,
    allUserChannelsRaw,
    channelWorkspaceMap,
  } = useChannelNavigation({ user });
  const {
    showLists,
    setShowLists,
    showCoworkers,
    setShowCoworkers,
    showLibrary,
    setShowLibrary,
    selectedListId,
    setSelectedListId,
    showAutomations,
    setShowAutomations,
    showSkills,
    setShowSkills,
    showConnectedTools,
    setShowConnectedTools,
    showInvites,
    setShowInvites,
    handleToggleAutomations,
    handleToggleInvites,
    closeAllSideViews,
    handleToggleLists,
    handleToggleCoworkers,
    handleSelectList,
  } = useTopLevelView({ setSelectedChannel });
  const {
    scope,
    setScope,
    selectedWorkspace,
    workspaceList,
    workspaceListStatus,
    handleSelectWorkspace,
    handleScopeChange,
    navigateToSubdomain,
    setPendingWorkspace,
    pendingWorkspaceId,
    terminalWorkspaceId,
  } = useWorkspaceScope({
    user,
    inviteToken,
    setSelectedChannel,
    closeAllSideViews,
  });

  // ─── openChannelWithContext — kept inline so it remains byte-identical ─
  // Touches setters across THREE state machines (scope, channel, top-level
  // views) plus two target setters; composing it here keeps the body the
  // same as before the extraction. See the App.tsx P5 plan for the
  // byte-identical contract.
  const openChannelWithContext = useCallback(
    (
      channelId: string,
      opts?: {
        workspaceId?: string | null;
        targetMessageId?: string;
        targetThreadId?: string;
      },
    ) => {
      csMark("click", channelId);
      // Split the channel-switch state bag by source:
      //
      // - Keyboard (Alt+↑/↓ via useCommandShortcuts) → `startTransition`.
      //   Rapid bursts otherwise produced ~60-100ms commits per big-
      //   channel switch (150-row MessageListView mount + reactions /
      //   attachments / link-previews emissions) that synchronously
      //   blocked the next queued keydown — `evDelay` of 100-300ms in
      //   the cs-profile logs. Marking the resulting setState bag as a
      //   transition makes the render interruptible: React yields
      //   between fibers, the next keydown dispatches immediately, and
      //   a newer transition discards the in-progress one — only the
      //   channel the user lands on completes its render.
      //
      // - Anything else (sidebar click, command palette, notification
      //   deeplink, programmatic) → urgent setState. Single-intent
      //   actions want instant feedback; transitions added a
      //   perceptible warm-path delay + InboxView flicker on these
      //   paths (which is why the wrap was removed pre-`csPeekClickSource`).
      //
      // The source comes from `csNoteClickFromEvent` set by the kbd
      // handler immediately before `run(commandId)` — see
      // `apps/desktop/src/hooks/useCommandShortcuts.ts`.
      const isKeyboardSwitch = csPeekClickSource() === "kbd";
      const applyStateBag = () => {
        if (opts?.workspaceId !== undefined && opts.workspaceId) {
          setScope(opts.workspaceId);
        }
        setSelectedChannel(channelId);
        setShowLists(false);
        setShowCoworkers(false);
        setShowAutomations(false);
        setShowSkills(false);
        setShowConnectedTools(false);
        setShowLibrary(false);
        setShowInvites(false);
        setSelectedListId(null);
        if (opts?.targetMessageId) {
          setTargetMessageId(opts.targetMessageId);
        }
        // Always update threadId — undefined must clear a previously
        // set value so switching channels doesn't leak a stale thread
        // intent.
        setTargetThreadId(opts?.targetThreadId ?? null);
      };
      if (isKeyboardSwitch) {
        startTransition(applyStateBag);
      } else {
        applyStateBag();
      }
    },
    // Empty deps preserves the byte-identical contract from the
    // pre-extraction implementation. All setters referenced are
    // React `useState` setters (stable identity by React's guarantee).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Bridge xterm deep-link clicks → openChannelWithContext. The xterm
  // link addon dispatches `ano:open-channel-from-shell` from outside the
  // React tree; this listener forwards the intent in-tree.
  useShellDeepLinkListener(openChannelWithContext);

  // Stable callback for MessageArea — avoids inline arrow function that
  // defeats React.memo on every parent re-render.
  const selectedWorkspaceRef = useRef(selectedWorkspace);
  selectedWorkspaceRef.current = selectedWorkspace;
  const handleSelectChannelForMessageArea = useCallback(
    (id: string) => {
      openChannelWithContext(id, {
        workspaceId: selectedWorkspaceRef.current,
      });
    },
    [openChannelWithContext],
  );

  // ─── Active view derivation (priority order is load-bearing) ─────────
  const views = useActiveView({
    scope,
    selectedChannel,
    selectedWorkspace,
    selectedListId,
    showLists,
    showCoworkers,
    showAutomations,
    showSkills,
    showConnectedTools,
    showLibrary,
    showInvites,
  });
  const { isMultiplexActive } = views;
  const { mountedIds } = useMultiplexMountedIds({
    selectedChannel,
    selectedWorkspace,
    channelWorkspaceMap,
    allUserChannelsRaw,
    isMultiplexActive,
  });
  const {
    terminalOpen,
    hasOpenedTerminal,
    terminalWidth,
    setTerminalWidth,
    showChat,
    showSidebar,
    handleToggleTerminal,
    handleCycleViewMode,
  } = useViewMode();
  const { handleInviteNavigation, handleInviteTokenConsumed } = useInviteFlow({
    user,
    workspaceList,
    inviteToken,
    setInviteToken,
    setScope,
    setSelectedChannel,
    setPendingWorkspace,
    navigateToSubdomain,
  });
  const dialogs = useDialogState();

  // ─── Cross-cutting effects ───────────────────────────────────────────
  useWarmPreload({ user, loading });
  // Reflect the workspace + open channel into the `/<slug>/c/<name>` URL (one
  // shareable shape with the public SEO scheme) + apply such a link on load.
  const workspaceSlug = useMemo(() => {
    const ws = workspaceList.find((w) => w.id === selectedWorkspace);
    return ws ? ws.slug || generateSlugFromName(ws.name ?? "") : null;
  }, [workspaceList, selectedWorkspace]);
  useAppUrlSync({
    user,
    selectedWorkspace,
    workspaceSlug,
    selectedChannel,
    setSelectedChannel,
  });
  useCliCredentialsInstall({ selectedWorkspace, workspaceList, user });
  const connectionState = useConnectionState();
  const isConnected = connectionState.name === "connected";
  useConnectionStatusToasts(isConnected);
  useUpdatePrompts(!!selectedWorkspace);
  const { bootstrapTimedOut } = useAppCrossCuttingEffects({
    user,
    loading,
    zero,
    selectedChannel,
    myMemberships,
  });
  // Single owner of the post-auth boot decision: a brand-new user (0
  // workspaces) gets the full-screen wizard INSTEAD of the app shell, the boot
  // cover stays continuous sign-in → wizard, and a stale persisted scope never
  // flashes a broken workspace shell. See components/app/CLAUDE.md "Boot flow".
  const firstRun = useFirstRunGate({
    userId: user?.id ?? "",
    userEmail: user?.email ?? "",
    firstName: user?.display_name?.split(" ")[0],
    selectedWorkspace,
    workspaceList,
    workspaceListStatus,
    bootstrapTimedOut,
  });
  // After a lurker signs up from a public-workspace landing, auto-join the
  // workspace they intended to join (one-shot; transitions in via Zero sync).
  useAutoJoinAfterAuth(user?.id);
  // After the auto-join lands, restore + publish any reply a logged-out
  // visitor stashed on a public-workspace page before signing up (one-shot;
  // gated on the new channel membership becoming visible to Zero).
  usePendingDraftReply(user?.id);
  // App-level notifications
  useNotifications({
    userId: user?.id,
    selectedChannel,
    mutedSet,
    openChannelWithContext,
  });
  // Ask the OS once for notification permission on first session after
  // the user signs in — keeps the install-day "macOS notifications off
  // by default" trap from silently squelching banners.
  useRequestNotificationPermission(!!user?.id);
  // Tab title + Electron dock badge driven by aggregate unread/mention counts.
  useChromeBadge(selectedChannel);
  // (Legacy `ano:coworker-chat` event opened the terminal AI chat with
  // coworker context. Coworker chat is now handled inline by the
  // canvas editor's "Test your coworker" panel — no terminal hop.)

  if (loading && !bootstrapTimedOut) {
    return <AuthLoadingFullscreen />;
  }

  if (loading && bootstrapTimedOut) {
    return (
      <AuthTimeoutScreen
        title="Loading is taking too long"
        description="The app could not finish initializing. Please retry or sign out."
        onSignOut={() => void logout()}
      />
    );
  }

  if (!user) {
    return (
      <ProfileInitializationState
        isLoggingOut={loggingOut}
        onRetry={() => window.location.reload()}
        onSignOut={() => void logout()}
      />
    );
  }

  // Public-workspace splash. When the signed-in user lands on a slug
  // that doesn't match any workspace in their membership-filtered
  // list, AND the slug isn't "home", treat it as a potential public
  // workspace and let the splash decide via /api lookup. This
  // replaces use-workspace-scope.ts's silent fall-through to home.
  const slugFromUrl = workspaceSlugFromUrl(window.location);
  // Mirror the scope-pinning predicate exactly (stored slug OR name-derived
  // slug, normalized) so a member never sees the public splash for their own
  // workspace reached via the name-derived URL.
  const normalizedUrlSlug = normalizeWorkspaceSlug(slugFromUrl ?? "");
  const slugMatchesMembership =
    !slugFromUrl ||
    slugFromUrl === "home" ||
    workspaceList.some(
      (w) =>
        w.slug === normalizedUrlSlug ||
        generateSlugFromName(w.name ?? "") === normalizedUrlSlug,
    );
  if (slugFromUrl && !slugMatchesMembership) {
    const waitingForJoinedWorkspace =
      selectedWorkspace !== null &&
      selectedWorkspace === pendingWorkspaceId &&
      !workspaceList.some((w) => w.id === selectedWorkspace);
    if (workspaceListStatus.type !== "complete" || waitingForJoinedWorkspace) {
      return <AuthLoadingFullscreen />;
    }
    return (
      <PublicWorkspaceSplash
        slug={slugFromUrl}
        onJoined={(workspaceId) => {
          setPendingWorkspace(workspaceId);
          handleSelectWorkspace(workspaceId);
        }}
        onCancel={() => {
          // Bounce to the app host root (home) so the splash unmounts.
          window.location.href = `${appOrigin()}/`;
        }}
      />
    );
  }

  // First-run gate (exclusive). For a new user we render the wizard — or the
  // continuous boot cover while the decision resolves — INSTEAD of the app
  // shell, so the shell never paints (no workspace/Home flash). A returning
  // user falls straight through to the shell below.
  if (firstRun.gate.kind !== "app") {
    return (
      <FirstRunGate
        firstRun={firstRun}
        onCreated={(id) => handleSelectWorkspace(id)}
      />
    );
  }

  const commandContext: CommandContext = {
    selectedChannelId: selectedChannel ?? undefined,
    selectedWorkspaceId: selectedWorkspace ?? undefined,
  };

  const handleOpenCommandPalette = () =>
    dialogs.setShowCommandPalette((prev) => !prev);
  const handleOpenKeyboardShortcuts = () =>
    dialogs.setShowKeyboardShortcuts(true);
  const handleCheckForUpdates = () => runUpdateCheck();

  // Group prop bundles for AppShell + AppDialogs to keep the JSX flat.
  // Wiring lives in the parent so every state setter referenced is in
  // scope; the children just consume the bundles.
  const shellNav = {
    scope,
    selectedWorkspace,
    selectedChannel,
    selectedListId,
    showLists,
    showCoworkers,
    showAutomations,
    showSkills,
    showConnectedTools,
    showLibrary,
    showInvites,
    handleScopeChange,
    setSelectedChannel,
    setShowLists,
    setShowCoworkers,
    setShowAutomations,
    setShowSkills,
    setShowConnectedTools,
    setShowLibrary,
    setShowInvites,
    setSelectedListId,
    navigateToSubdomain,
    handleSelectWorkspace,
    openChannelWithContext,
    handleToggleLists,
    handleToggleCoworkers,
    handleToggleAutomations,
    handleToggleInvites,
    setScope,
    workspaceList,
    setPendingWorkspace,
    setMembershipLostWorkspace: dialogs.setMembershipLostWorkspace,
    setListItemCreatePreselect: dialogs.setListItemCreatePreselect,
    setShowListItemCreate: dialogs.setShowListItemCreate,
  };
  const shellMultiplex = {
    mountedIds,
    channelWorkspaceMap,
    targetMessageId,
    targetThreadId,
    handleSelectChannelForMessageArea,
    handleToggleTerminal,
    handleTargetMessageHandled,
    handleSelectList,
  };
  const shellInbox = {
    inviteToken,
    handleInviteNavigation,
    handleInviteTokenConsumed,
  };
  const shellTerminal = {
    terminalWorkspaceId,
    terminalOpen,
    hasOpenedTerminal,
    terminalWidth,
    setTerminalWidth,
    handleOpenWorkspaceSettings: dialogs.handleOpenWorkspaceSettings,
    handleCycleViewMode,
  };
  const dialogList = {
    selectedWorkspace,
    showListItemCreate: dialogs.showListItemCreate,
    setShowListItemCreate: dialogs.setShowListItemCreate,
    listItemCreatePreselect: dialogs.listItemCreatePreselect,
    setListItemCreatePreselect: dialogs.setListItemCreatePreselect,
    setSelectedListId_FromCreate: (id: string) => {
      setSelectedListId(id);
      setShowLists(false);
      setShowCoworkers(false);
      setShowLibrary(false);
      setShowSkills(false);
      setShowConnectedTools(false);
    },
    showListCreate: dialogs.showListCreate,
    setShowListCreate: dialogs.setShowListCreate,
  };

  return (
    <CommandProvider context={commandContext}>
      <AppCommandBindings
        onOpenCommandPalette={handleOpenCommandPalette}
        onCycleViewMode={handleCycleViewMode}
        onToggleTerminal={() => void handleToggleTerminal()}
        onOpenKeyboardShortcuts={handleOpenKeyboardShortcuts}
        onCheckForUpdates={handleCheckForUpdates}
      />
      <AppShell
        connectionState={connectionState}
        isConnected={isConnected}
        appVersion={__APP_VERSION__}
        showSidebar={showSidebar}
        showChat={showChat}
        nav={shellNav}
        views={views}
        multiplex={shellMultiplex}
        inbox={shellInbox}
        terminal={shellTerminal}
      />
      <AppDialogs
        user={user}
        showChat={showChat}
        terminal={{
          terminalWorkspaceId,
          terminalOpen,
          terminalWidth,
          handleToggleTerminal,
        }}
        commandPalette={{
          showCommandPalette: dialogs.showCommandPalette,
          setShowCommandPalette: dialogs.setShowCommandPalette,
          workspaceList,
          navigateToSubdomain,
          setScope,
          openChannelWithContext,
        }}
        membershipLost={{
          membershipLostWorkspace: dialogs.membershipLostWorkspace,
          setMembershipLostWorkspace: dialogs.setMembershipLostWorkspace,
          setSelectedChannel,
          setShowLists,
          setShowCoworkers,
          setShowSkills,
          setShowConnectedTools,
          setShowLibrary,
          setSelectedListId,
          setScope,
          navigateToSubdomain,
        }}
        list={dialogList}
        showKeyboardShortcuts={dialogs.showKeyboardShortcuts}
        setShowKeyboardShortcuts={dialogs.setShowKeyboardShortcuts}
      />
    </CommandProvider>
  );
}

function App() {
  return (
    <AuthGate>
      <AuthenticatedApp />
    </AuthGate>
  );
}

export default App;
